/**
 * Streaming service for external (embed) chat.
 *
 * Calls the platform-api external-chat SSE endpoint, authenticating
 * with an embed API key (eak_...) instead of a user session token.
 * The response is an SSE stream from Claude via the Archestra MCP Gateway.
 *
 * SSE event format from backend:
 *   {"type": "start", "id": "msg_..."}
 *   {"type": "status", "text": "..."}  — agent thinking/searching (transient)
 *   {"type": "text", "text": "..."}    — final response content
 *   {"type": "done"}
 *   {"type": "error", "error": "..."}
 *   {"error": "..."}
 */

import * as Sentry from '@sentry/react';
import { SearchResultsEvent } from './types';

// ---------------------------------------------------------------------------
// Shared SSE helpers
// ---------------------------------------------------------------------------

/**
 * Read SSE lines from a ReadableStream and dispatch parsed JSON events.
 *
 * @returns `true` if the stream ended naturally (reader exhausted),
 *          `false` if the callback signalled an early stop.
 */
async function readSSELines(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  onEvent: (event: Record<string, unknown>) => boolean,
): Promise<boolean> {
  const decoder = new TextDecoder();
  let buf = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) return true;

    buf += decoder.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop() || '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (!raw) continue;

      try {
        const d = JSON.parse(raw);
        if (!onEvent(d)) return false;
      } catch {
        // Skip non-JSON lines
      }
    }
  }
}

async function fetchSSE(
  url: string,
  apiKey: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<
  | { ok: true; reader: ReadableStreamDefaultReader<Uint8Array> }
  | { ok: false; error: string }
> {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${apiKey}`,
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    let detail = `Request failed (${response.status})`;
    try {
      const parsed = JSON.parse(errText);
      detail = parsed.detail || parsed.error || parsed.message || detail;
    } catch {
      if (errText) detail = errText.slice(0, 300);
    }
    return { ok: false, error: detail };
  }

  const reader = response.body?.getReader();
  if (!reader) {
    return { ok: false, error: 'Streaming not supported in this browser' };
  }

  return { ok: true, reader };
}

// ---------------------------------------------------------------------------
// Chat streaming
// ---------------------------------------------------------------------------

export interface StreamExternalChatParams {
  orgId: string;
  collectionId: string;
  apiKey: string;
  message: string;
  conversationId?: string;
  searchContext?: string;
  signal?: AbortSignal;
  onToken: (token: string) => void;
  onStatus?: (status: string) => void;
  onError: (error: string) => void;
  onDone: (fullMessage: string, conversationId: string) => void;
}

export async function streamExternalChat({
  orgId,
  collectionId,
  apiKey,
  message,
  conversationId,
  searchContext,
  signal,
  onToken,
  onStatus,
  onError,
  onDone,
}: StreamExternalChatParams): Promise<void> {
  const url = `/proxy/platform/${encodeURIComponent(orgId)}/collections/${encodeURIComponent(collectionId)}/external-chat`;

  try {
    const result = await fetchSSE(
      url,
      apiKey,
      {
        message,
        conversation_id: conversationId || undefined,
        search_context: searchContext || undefined,
      },
      signal,
    );

    if (!result.ok) {
      onError(result.error);
      return;
    }

    let fullMessage = '';
    let responseConversationId = conversationId || '';

    const streamEnded = await readSSELines(result.reader, (d) => {
      if (d.error) {
        onError(d.error as string);
        return false;
      }
      if (d.type === 'start') {
        // Prefer explicit conversation_id; fall back to id for backwards compat
        if (d.conversation_id) {
          responseConversationId = d.conversation_id as string;
        } else if (d.id) {
          responseConversationId = d.id as string;
        }
        return true;
      }
      if (d.type === 'done' && d.conversation_id) {
        responseConversationId = d.conversation_id as string;
      }
      if (d.type === 'status' && d.text) {
        onStatus?.(d.text as string);
        return true;
      }
      if (d.type === 'text' && d.text) {
        fullMessage += d.text as string;
        onToken(d.text as string);
        return true;
      }
      if (d.type === 'done') {
        onDone(fullMessage.trim(), responseConversationId);
        return false;
      }
      return true;
    });

    // Stream ended without a done event — still signal completion
    if (streamEnded) {
      onDone(fullMessage.trim(), responseConversationId);
    }
  } catch (err) {
    if (signal?.aborted) return;
    Sentry.captureException(err, {
      extra: { context: 'stream-external-chat', orgId, collectionId },
    });
    onError(err instanceof Error ? err.message : String(err));
  }
}

// ---------------------------------------------------------------------------
// Search streaming
// ---------------------------------------------------------------------------

export interface StreamSearchParams {
  orgId: string;
  collectionId: string;
  apiKey: string;
  query: string;
  page?: number;
  filters?: Record<string, string>;
  skipSummary?: boolean;
  signal?: AbortSignal;
  onResults: (results: SearchResultsEvent) => void;
  onToken: (token: string) => void;
  onStatus?: (status: string) => void;
  onError: (error: string) => void;
  onDone: (fullSummary: string) => void;
}

export async function streamSearch({
  orgId,
  collectionId,
  apiKey,
  query,
  page,
  filters,
  skipSummary,
  signal,
  onResults,
  onToken,
  onStatus,
  onError,
  onDone,
}: StreamSearchParams): Promise<void> {
  const url = `/proxy/platform/${encodeURIComponent(orgId)}/collections/${encodeURIComponent(collectionId)}/external-search`;

  try {
    const result = await fetchSSE(
      url,
      apiKey,
      { query, page, filters, skip_summary: skipSummary },
      signal,
    );

    if (!result.ok) {
      onError(result.error);
      return;
    }

    let fullSummary = '';

    const streamEnded = await readSSELines(result.reader, (d) => {
      if (d.error) {
        onError(d.error as string);
        return false;
      }
      if (d.type === 'results') {
        onResults(d as unknown as SearchResultsEvent);
        return true;
      }
      if (d.type === 'status' && d.text) {
        onStatus?.(d.text as string);
        return true;
      }
      if (d.type === 'text' && d.text) {
        fullSummary += d.text as string;
        onToken(d.text as string);
        return true;
      }
      if (d.type === 'done') {
        onDone(fullSummary.trim());
        return false;
      }
      return true;
    });

    // Stream ended without a done event — still signal completion
    if (streamEnded) {
      onDone(fullSummary.trim());
    }
  } catch (err) {
    if (signal?.aborted) return;
    Sentry.captureException(err, {
      extra: { context: 'stream-external-search', orgId, collectionId },
    });
    onError(err instanceof Error ? err.message : String(err));
  }
}
